import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TextFieldModule } from '@angular/cdk/text-field';
import { take } from 'rxjs';
import { APIResponse } from 'fakturnia-shared';
import { ApiService } from 'shared/services/api.service';
import { SnackbarService } from 'shared/services/snackbar.service';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    TextFieldModule
  ],
  selector: 'app-send-notification',
  templateUrl: './send-notification.component.html'
})
export class SendNotificationComponent {

  isSending = false;
  isSendingTest = false;
  lastResult: any = null;

  form = new FormGroup({
    title: new FormControl('', [Validators.required, Validators.minLength(1), Validators.maxLength(200)]),
    message: new FormControl('', [Validators.required, Validators.minLength(1), Validators.maxLength(1000)]),
    saveToNotifications: new FormControl(true),
    sendToBrowsers: new FormControl(true),
    sendToMobile: new FormControl(true),
  });

  testEmailControl = new FormControl('', [Validators.required, Validators.email]);

  constructor(
    private _apiService: ApiService,
    private _snackbarService: SnackbarService
  ) {}

  send() {
    if (this.form.invalid) {
      this._markFormGroupTouched(this.form);
      this._snackbarService.showSnackBar('Proszę wypełnić wymagane pola', 'error', 3000);
      return;
    }

    if (!confirm('Czy na pewno chcesz wysłać to powiadomienie do wszystkich użytkowników?')) {
      return;
    }

    this.isSending = true;
    this.lastResult = null;

    this._apiService.sendAdminNotification(this.form.value as any).pipe(take(1)).subscribe({
      next: (response: APIResponse) => {
        this.isSending = false;
        if (response.success) {
          this.lastResult = response.data;
          this._snackbarService.showSnackBar('Powiadomienie zostało wysłane', 'success', 3000);
          this.form.reset({ saveToNotifications: true, sendToBrowsers: true, sendToMobile: true });
        } else {
          this._snackbarService.showSnackBar(response.message || 'Błąd wysyłki', 'error', 3000);
        }
      },
      error: () => {
        this.isSending = false;
        this._snackbarService.showSnackBar('Błąd podczas wysyłania powiadomienia', 'error', 3000);
      }
    });
  }

  private _markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this._markFormGroupTouched(control);
      }
    });
  }

  sendTestEmail() {
    // Walidacja: tytuł + treść z głównego formularza są wymagane do wysłania testu
    const titleCtrl = this.form.controls.title;
    const messageCtrl = this.form.controls.message;

    if (titleCtrl.invalid || messageCtrl.invalid) {
      titleCtrl.markAsTouched();
      messageCtrl.markAsTouched();
      this._snackbarService.showSnackBar('Wypełnij tytuł i treść powiadomienia', 'error', 3000);
      return;
    }

    if (this.testEmailControl.invalid) {
      this.testEmailControl.markAsTouched();
      this._snackbarService.showSnackBar('Podaj prawidłowy adres email', 'error', 3000);
      return;
    }

    this.isSendingTest = true;

    this._apiService.sendAdminTestPush({
      email: this.testEmailControl.value!.trim(),
      title: titleCtrl.value!,
      message: messageCtrl.value!
    }).pipe(take(1)).subscribe({
      next: (response: APIResponse) => {
        this.isSendingTest = false;
        if (response.success) {
          // Komunikat z backendu już zawiera szczegóły per kanał
          this._snackbarService.showSnackBar(response.message || 'Wysłano testowe powiadomienie', 'success', 8000);
        } else {
          this._snackbarService.showSnackBar(response.message || 'Błąd wysyłki testowego powiadomienia', 'error', 5000);
        }
      },
      error: () => {
        this.isSendingTest = false;
        this._snackbarService.showSnackBar('Błąd podczas wysyłania testowego powiadomienia', 'error', 4000);
      }
    });
  }
}
